import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
} from "@mui/material";
import { useAtom } from "jotai";
import {
  FileUpload,
  FileUploadRef,
  PendingFile,
} from "../../../components/ImageUpload";
import {
  UrlImageUpload,
  UrlImageUploadRef,
} from "../../../components/UrlImageUpload";
import { CloseButton } from "../../../components/buttons/CloseButton";
import { ProcessButton } from "../../../components/buttons/ProcessButton";
import {
  ROOT_FOLDER,
  combinePath,
  getImageUrl,
} from "../../../features/images/image";
import { addImageToDb } from "../../../features/images/image.service";
import { imageDbAtom } from "../../../features/images/image.state";
import { useImages } from "../../../features/images/image-func";
import { FolderSelect } from "../image-components";
import { ImageModalParams, ImageModal } from "./ImageModal";
import { useSnackbar } from "notistack";

export const ImageUploadModal = (params: ImageModalParams) => {
  return (
    <ImageModal
      open={!!params.options}
      title="Image Upload"
      onClose={params.onClose}
    >
      <DialogTitle>
        <div>Upload file{params.options?.multi ? "s" : ""}</div>
        <CloseButton onClose={params.onClose} />
      </DialogTitle>
      <ImageUploadContent {...params} />
    </ImageModal>
  );
};

export const ImageUploadContent = ({ options, onClose }: ImageModalParams) => {
  const [imageDb, setImageDb] = useAtom(imageDbAtom);
  const { reloadImageDb } = useImages();
  const fileUploadRef = useRef<FileUploadRef>(null);
  const urlUploadRef = useRef<UrlImageUploadRef>(null);
  const [tabValue, setTabValue] = useState(0);

  const [uploadOptions, setUploadOptions] = useState({
    ...options,
    folder: options?.folder || ROOT_FOLDER,
  });
  const [uploading, setUploading] = useState(false);
  const [count, setCount] = useState(0);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  // FilePond id -> the name we will actually upload under, once "Rename to keep
  // both" has run. Keyed by id, not name, because the colliding case includes the
  // same name twice in one batch.
  const [renamed, setRenamed] = useState<Record<string, string>>({});

  const { enqueueSnackbar } = useSnackbar();

  const uploadFolder =
    uploadOptions.folder === ROOT_FOLDER ? "/" : uploadOptions.folder;

  // Uploading a name that already exists REPLACES that blob, and the image field on
  // every product stores only the path — so all of them silently switch to the new
  // picture (Punjab #317193: 54 shared paths across 123 products, from files named
  // 1.png, 2.png, …). The server does not rename on collision and does not warn, so
  // this is the only place the user finds out before the old picture is gone.
  //
  // Two ways to collide, and both land on the same blob:
  //   - `library` — the name is already in this folder.
  //   - `batch`   — the same name appears twice in this upload. FilePond uploads in
  //                 parallel, so the two writes race on one blob and Azure fails the
  //                 loser with 412 ConditionNotMet (the SDK commits under If-Match).
  //
  // Skipped while uploading: `onFileUpload` adds each finished file to `imageMap`,
  // so a live recompute would flag this batch's own files as duplicates of
  // themselves. Skipped when `options.path` is set — that is the deliberate
  // Re-upload/editor flow, where replacing the blob everywhere is the point.
  const collisions = useMemo(() => {
    if (options?.path || tabValue !== 0 || uploading || !pendingFiles.length)
      return [];
    const seen = new Set<string>();
    const found: { name: string; reason: "library" | "batch" }[] = [];
    pendingFiles.forEach((f) => {
      const name = renamed[f.id] ?? f.name;
      if (!name) return;
      const path = combinePath(uploadFolder, name);
      if (seen.has(path)) found.push({ name, reason: "batch" });
      else if (imageDb?.imageMap?.has(path))
        found.push({ name, reason: "library" });
      seen.add(path);
    });
    return found;
  }, [
    options?.path,
    tabValue,
    uploading,
    pendingFiles,
    renamed,
    uploadFolder,
    imageDb?.imageMap,
  ]);

  // Give every colliding file the first free `name-N.ext`, checking the library AND
  // the names this same batch has already claimed — otherwise two collisions in one
  // upload both resolve to `-1` and race each other exactly as before.
  const renameToKeepBoth = () => {
    const taken = new Set<string>();
    const next: Record<string, string> = {};
    pendingFiles.forEach((f) => {
      let name = renamed[f.id] ?? f.name;
      const dot = name.lastIndexOf(".");
      const stem = dot > 0 ? name.slice(0, dot) : name;
      const ext = dot > 0 ? name.slice(dot) : "";
      let i = 0;
      while (
        taken.has(combinePath(uploadFolder, name)) ||
        imageDb?.imageMap?.has(combinePath(uploadFolder, name))
      ) {
        name = `${stem}-${++i}${ext}`;
      }
      taken.add(combinePath(uploadFolder, name));
      next[f.id] = name;
    });
    setRenamed(next);
    fileUploadRef.current?.applyNames(next);
  };

  const renames = pendingFiles
    .filter((f) => renamed[f.id] && renamed[f.id] !== f.name)
    .map((f) => renamed[f.id]);

  useEffect(() => {
    if (!options) {
      setUploading(false);
      fileUploadRef.current?.reset();
      urlUploadRef.current?.reset();
      setTabValue(0);
      setPendingFiles([]);
      setRenamed({});
    }
  }, [options]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setCount(0);
    setPendingFiles([]);
    setRenamed({});
  };

  const handleUpload = () => {
    setUploading(true);
    if (tabValue === 0) {
      fileUploadRef.current?.upload();
    } else {
      urlUploadRef.current?.upload();
    }
  };

  return (
    <>
      <DialogContent>
        {(!options.path || options.canSelectFolder) && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <FolderSelect
              folder={uploadOptions.folder}
              label="Upload to a folder"
              onChange={(f) =>
                setUploadOptions({ ...uploadOptions, folder: f })
              }
            />
          </Box>
        )}

        {collisions.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>
              {collisions.length === 1
                ? "This name is already used"
                : `${collisions.length} of these names are already used`}
            </AlertTitle>
            Uploading will replace the existing image, and every product using it
            will show the new picture.
            {collisions.some((c) => c.reason === "batch") &&
              " Some of these files have the same name as each other, so they would also overwrite one another."}
            <Box
              component="ul"
              sx={{ mt: 1, mb: 0, pl: 2.5, wordBreak: "break-all" }}
            >
              {collisions.slice(0, 5).map((c, i) => (
                <li key={`${c.name}-${i}`}>
                  {c.name}
                  {c.reason === "batch" && " (twice in this upload)"}
                </li>
              ))}
              {collisions.length > 5 && (
                <li>and {collisions.length - 5} more</li>
              )}
            </Box>
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={renameToKeepBoth}
              sx={{ mt: 1.5 }}
            >
              Rename to keep both
            </Button>
          </Alert>
        )}

        {!collisions.length && renames.length > 0 && !uploading && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Renamed so nothing is overwritten:{" "}
            <Box component="span" sx={{ wordBreak: "break-all" }}>
              {renames.slice(0, 5).join(", ")}
              {renames.length > 5 && ` and ${renames.length - 5} more`}
            </Box>
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="upload tabs"
          >
            <Tab label="Upload Files" />
            <Tab label="From URL" />
          </Tabs>
        </Box>

        {/* Always render both components but hide inactive - prevents FilePond DOM conflict on unmount */}
        <Box sx={{ display: tabValue === 0 ? "block" : "none" }}>
          <FileUpload
            ref={fileUploadRef}
            options={uploadOptions}
            minHeight="50vh"
            onUploadFinish={(success) => {
              setUploading(false);
              if (success) {
                // Refetch the full library after the batch completes. Per-file
                // onFileUpload closes over a stale `imageDb`, so in a multi-file upload
                // its optimistic writes clobber each other and only the last image
                // survives in the picker (I18 — drove users to re-upload duplicates).
                // The authoritative reload restores all of them and picks up any
                // server-side name sanitising. Note the server does NOT rename on
                // collision — it deletes the existing blob and writes over it — so a
                // reload cannot surface a clobbered image. That is what the
                // duplicate-name warning above is for.
                reloadImageDb();
                onClose();
              } else
                enqueueSnackbar("Error uploading image", { variant: "error" });
            }}
            onFileUpload={(path, fileSize, error) => {
              // A process with no error can still hand back an empty serverId (CM2-2RY):
              // there is no path to add, and the batch reload picks the image up anyway.
              if (!error && path) {
                // Optimistic add for instant feedback; the authoritative refresh of the
                // full library happens once per batch in onUploadFinish (reloadImageDb).
                const db = addImageToDb(imageDb, path, fileSize);
                setImageDb(db);
                options.onSelect?.(path, getImageUrl(db.storageUrl, path));
              }
            }}
            onChange={(c, files) => {
              setCount(c);
              setPendingFiles(files ?? []);
            }}
          />
        </Box>

        <Box sx={{ display: tabValue === 1 ? "block" : "none" }}>
          <UrlImageUpload
            ref={urlUploadRef}
            options={uploadOptions}
            minHeight="50vh"
            onUploadFinish={(success) => {
              setUploading(false);
              if (success) {
                // Refetch the full library after the batch completes. Per-file
                // onFileUpload closes over a stale `imageDb`, so in a multi-file upload
                // its optimistic writes clobber each other and only the last image
                // survives in the picker (I18 — drove users to re-upload duplicates).
                // The authoritative reload restores all of them and picks up any
                // server-side name sanitising. Note the server does NOT rename on
                // collision — it deletes the existing blob and writes over it — so a
                // reload cannot surface a clobbered image. That is what the
                // duplicate-name warning above is for.
                reloadImageDb();
                onClose();
              } else
                enqueueSnackbar("Error uploading image", { variant: "error" });
            }}
            onFileUpload={(path, fileSize, error) => {
              // A process with no error can still hand back an empty serverId (CM2-2RY):
              // there is no path to add, and the batch reload picks the image up anyway.
              if (!error && path) {
                // Optimistic add for instant feedback; the authoritative refresh of the
                // full library happens once per batch in onUploadFinish (reloadImageDb).
                const db = addImageToDb(imageDb, path, fileSize);
                setImageDb(db);
                options.onSelect?.(path, getImageUrl(db.storageUrl, path));
              }
            }}
            onChange={(c) => setCount(c)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <ProcessButton
          processing={uploading}
          color="primary"
          onClick={handleUpload}
          label={
            tabValue === 0
              ? `Upload ${count > 1 ? count + " images" : ""}`
              : count > 0
              ? "Upload from URL"
              : "Upload"
          }
          disabled={!count || !uploadOptions.folder}
        />
      </DialogActions>
    </>
  );
};
